import { ArrowUpTrayIcon } from '@heroicons/react/24/outline'
import StoreOrderProblem from '@/dealers/components/StoreOrderProblem'
import Banner from '@/gf/components/Banner'
import ButtonLink from '@/gf/components/ButtonLinkOld'
import Button from '@/gf/components/ButtonOld'
import Card from '@/gf/components/Card'
import Checkbox from '@/gf/components/Checkbox'
import Layout from '@/gf/components/Layout'
import Link from '@/gf/components/Link'
import Modal from '@/gf/components/Modal'
import ModalForm from '@/gf/components/ModalForm'
import Spinner from '@/gf/components/Spinner'
import TextInput from '@/gf/components/TextInput'
import Tooltip from '@/gf/components/Tooltip'
import InvoiceInput from '@/gf/components/inputs/InvoiceInput'
import PriceInput from '@/gf/components/inputs/Price'
import useConfig from '@/gf/hooks/useConfig'
import useMsgs from '@/gf/hooks/useMsgs'
import useTimelineEvents, { TIMELINE_QUERY } from '@/gf/hooks/useTimelineEvents'
import useToggle from '@/gf/hooks/useToggle'
import useTransactionsSearch from '@/gf/hooks/useTransactionsSearch'
import useUppy from '@/gf/hooks/useUppy'
import InvoiceM from '@/gf/modules/Invoice'
import MoneyM from '@/gf/modules/Money'
import RequestForQuoteContext from '@/gf/modules/RequestForQuote'
import TimeM from '@/gf/modules/Time'
import UserM from '@/gf/modules/User'
import { MARK_AS_SHIPPED, SET_GEARFLOW_SHIPPPING_STORE_ORDER } from '@/gf/mutations/shipping'
import {
  PaymentMethod,
  useApplyCustomerDiscountMutation,
  useReplaceInvoiceDocumentMutation,
  useUploadInvoicePayDirectMutation,
} from '@/suppliers/_gen/gql'
import useSession from '@/suppliers/hooks/useSession'
import * as api from '@/suppliers/modules/api'
import { ApolloError, useMutation } from '@apollo/client'
import { GraphQLError } from 'graphql'
import { DateTime } from 'luxon'
import pluralize from 'pluralize'
import { useState } from 'react'
import { Route, Routes, useNavigate } from 'react-router-dom'
import { Shipment } from '../../../types'
import AccountMachinesTable from '../../components/AccountMachinesTable'
import CancelOrderModal from '../../components/CancelOrderModal'
import EditShippingModalForm from '../../components/EditShippingModalForm'
import EmailActionModal from '../../components/EmailActionModal'
import EmailActionModalForm from '../../components/EmailActionModalForm'
import OfflineBuyerTransactionModalForm from '../../components/OfflineBuyerTransactionModalForm'
import OfflineTransactionCard from '../../components/OfflineTransactionCard'
import OfflineTransactionModalForm from '../../components/OfflineTransactionModalForm'
import useApproveStoreOrder from '../../hooks/useApproveStoreOrder'
import useGqlClient from '../../hooks/useGqlClient'
import useStoreOrderReadyForPickup from '../../hooks/useStoreOrderReadyForPickup'
import QueryString from '../../modules/QueryString'
import StoreOrderM from '../../modules/StoreOrder'
import MARK_INVOICE_PAID from '../../mutations/storeOrders'
import CompleteOrder from './Complete'
import CustomerCard from './CustomerCard'
import OrderItemCards from './OrderItemCards'
import ProofOfDeliveries from './ProofOfDeliveries'
import RefundOrderModal from './RefundOrderModal'
import TimelineCard from './TimelineCard'
import UploadPod from './UploadPod'
import { useOrderContext } from './context'

const attachmentAllowedFileTypes = ['.jpg', '.jpeg', '.png', '.pdf']

const StoreOrderDetails = () => {
  const { buyersUrl, adminUrl } = useConfig()
  const { user } = useSession()
  const { storeOrderId, storeOrder, storeOrderOld, refetch } = useOrderContext()
  const client = useGqlClient()
  const navigate = useNavigate()
  const [_msgs, msgsMgr] = useMsgs()
  const [markPaidModalOpen, markPaidModalToggler] = useToggle()
  const [offlineTransactionModalOpen, offlineTransactionModalToggler] = useToggle()
  const [offlineBuyerTransactionModalOpen, offlineBuyerTransactionModalToggler] = useToggle()
  const [approveModalOpen, approveModalToggler] = useToggle()
  const [cancelModalOpen, cancelModalToggler] = useToggle()
  const [spinnerLive, spinner] = useToggle()
  const [alertPendingCharges, pendingChargesAlerter] = useToggle()
  const [shipmentToEdit, setShipmentToEdit] = useState<Shipment>()

  const [approvedEmailAdditionalCommentsText, setApprovedEmailAdditionalCommentsText] = useState('')
  const [approvedEmailCheckbox, setApprovedEmailCheckbox] = useState<boolean>(true)
  const [additionalChargeModalOpen, additionalChargeModalToggler] = useToggle()
  const [additionalChargeName, setAdditionalChargeName] = useState('')
  const [additionalChargePriceText, setAdditionalChargePriceText] = useState('')
  const [additionalChargeEmailNotes, setAdditionalChargeEmailNotes] = useState('')
  const [additionalChargeEmailCheckbox, setAdditionalChargeEmailCheckbox] = useState(true)
  const [shippingModalShipment, setShippingModalShipment] = useState<Shipment>()
  const [invoiceDueDate, setInvoiceDueDate] = useState<DateTime>()

  const [shippingEmailAdditionalCommentsText, setShippingEmailAdditionalCommentsText] = useState('')

  const [shippingEmailCheckbox, setShippingEmailCheckbox] = useState<boolean>(true)
  const [markAsShipped] = useMutation(MARK_AS_SHIPPED, { refetchQueries: [TIMELINE_QUERY] })
  const [setGearflowShippingMutation] = useMutation(SET_GEARFLOW_SHIPPPING_STORE_ORDER)
  const [invoicePaidMutation] = useMutation(MARK_INVOICE_PAID)
  const [changeGearflowShippingModalOpen, changeGearflowShippingModalToggle] = useToggle()
  const [readyForPickupModalOpen, readyForPickupModalToggler] = useToggle()
  const [readyForPickupEmailNotes, setReadyForPickupEmailNotes] = useState('')
  const [readyForPickupEmailCheckbox, setReadyForPickupEmailCheckbox] = useState(true)
  const [invoiceModalOpen, invoiceModalToggler] = useToggle()
  const [replaceInvoiceModalOpen, replaceInvoiceModalToggler] = useToggle()

  const [approveStoreOrder] = useApproveStoreOrder([TIMELINE_QUERY])

  const [uploadInvoicePayDirect] = useUploadInvoicePayDirectMutation({
    client,
    refetchQueries: [TIMELINE_QUERY],
  })

  const [replaceInvoiceDocument] = useReplaceInvoiceDocumentMutation({
    client,
    refetchQueries: [TIMELINE_QUERY],
  })

  const [storeOrderReadyForPickup] = useStoreOrderReadyForPickup([TIMELINE_QUERY])
  const uppy = useUppy({
    allowedFileTypes: attachmentAllowedFileTypes,
    autoProceed: false,
    uploadContext: 'supplier_order_attachment',
  })
  // Separate uploader for the admin "replace invoice document" flow so its
  // selected file never collides with the supplier "send invoice" uploader.
  const replaceUppy = useUppy({
    allowedFileTypes: attachmentAllowedFileTypes,
    autoProceed: false,
    uploadContext: 'supplier_order_attachment',
  })

  const offlineTransactions = useTransactionsSearch({
    page: 1,
    filter: ['and', ['equals', 'store_order_id', storeOrderOld.id], ['equals', 'offline', 'true']],
  })

  const timelineEventsResult = useTimelineEvents(storeOrderOld.id)
  const eventsSearch = timelineEventsResult.data?.eventsSearch

  const isUnpaid = UserM.isAdmin(user)
    ? StoreOrderM.isUnpaid(storeOrderOld)
    : StoreOrderM.isUnapprovedBySupplier(storeOrderOld)

  const subtotal = StoreOrderM.subtotal(storeOrderOld)
  const additionalCharges = storeOrderOld.additionalCharges.map((ac) => ac.price)

  const totalForConvenienceFee = [
    subtotal,
    storeOrderOld.shippingAmount,
    storeOrderOld.salesTax,
  ].reduce(MoneyM.add)

  const convenienceFee =
    storeOrderOld.paymentMethod !== PaymentMethod.Direct
      ? MoneyM.mult(totalForConvenienceFee, storeOrderOld.convenienceFeeRate)
      : MoneyM.fromInt(0, 'USD')
  const total = [totalForConvenienceFee, convenienceFee, ...additionalCharges].reduce(MoneyM.add)

  const paidAdditionalChargesPrice = storeOrderOld.additionalCharges
    .filter((ac) => ac.state === 'charged')
    .map((ac) => ac.price)
    .reduce(MoneyM.add, MoneyM.fromDecimal(0, 'USD'))

  const paidTotal = MoneyM.add(
    StoreOrderM.isUnapprovedBySupplier(storeOrderOld)
      ? MoneyM.fromDecimal(0, 'USD')
      : [subtotal, storeOrderOld.shippingAmount, storeOrderOld.salesTax, convenienceFee].reduce(
          MoneyM.add
        ),
    paidAdditionalChargesPrice
  )

  const totalRefunded = storeOrderOld.refunds
    .map((r) => r.amount)
    .reduce(MoneyM.add, MoneyM.fromDecimal(0, 'USD'))

  const refundableAmount = (() => {
    const result = MoneyM.sub(paidTotal, totalRefunded)
    return storeOrderOld.features.txns ? result : MoneyM.sub(result, paidAdditionalChargesPrice)
  })()

  const netTotal = [total, totalRefunded].reduce(MoneyM.sub)
  const netCustomerPayment = [paidTotal, totalRefunded].reduce(MoneyM.sub)

  const gearflowShippingFee = storeOrderOld.gearflowShipping
    ? storeOrderOld.shippingAmount
    : MoneyM.fromDecimal(0, 'USD')

  const payoutEligible = MoneyM.max(
    [
      isUnpaid ? netTotal : netCustomerPayment,
      gearflowShippingFee,
      convenienceFee,
      storeOrderOld.salesTax,
    ].reduce(MoneyM.sub),
    MoneyM.fromDecimal(0, 'USD')
  )

  const takeRate = Math.round(10_000 * (1 - storeOrderOld.payoutRate)) / 10_000
  const gearflowFee = MoneyM.mult(payoutEligible, takeRate)

  const payout = [payoutEligible, gearflowFee].reduce(MoneyM.sub)

  const vendorAccountNumber =
    storeOrderOld?.vendor?.accountNumbers && storeOrderOld?.vendor?.accountNumbers.length > 0
      ? ` (account number: ${storeOrderOld.vendor.accountNumbers.join(', ')})`
      : ''

  const paidOnAccountText =
    storeOrderOld.paymentMethod === PaymentMethod.Direct
      ? ' (paid direct)'
      : storeOrderOld.paymentMethod === PaymentMethod.Invoice
        ? ' (paid on invoice)'
        : ''

  const paymentCardTitle =
    storeOrderOld.state === 'CANCELLED'
      ? 'Cancelled'
      : storeOrderOld.state === 'REFUNDED'
        ? `Refunded${paidOnAccountText}`
        : storeOrderOld.paymentMethod === PaymentMethod.Direct
          ? `Direct Pay ${vendorAccountNumber}`
          : isUnpaid
            ? `Awaiting Payment${paidOnAccountText}`
            : `Paid${paidOnAccountText}`

  const approveAction = () => {
    spinner.on()
    approveStoreOrder({
      variables: {
        storeOrderId: storeOrderOld.id,
        notes: approvedEmailAdditionalCommentsText,
        sendEmail: approvedEmailCheckbox,
      },
    })
      .then(() => {
        msgsMgr.add(
          `Order approved.${approvedEmailCheckbox ? ' Email sent to customer.' : ''}`,
          'positive'
        )
        setApprovedEmailAdditionalCommentsText('')
        refetch()
      })
      .catch((error: ApolloError) => msgsMgr.add(error.message, 'negative'))
      .finally(() => {
        approveModalToggler.off()
        spinner.off()
      })
  }

  const addAdditionalChargeAction = async () => {
    spinner.on()
    const error = await api.addAdditionalCharge(
      storeOrderOld.id,
      additionalChargeName,
      Math.round(parseFloat(additionalChargePriceText) * 100),
      additionalChargeEmailCheckbox,
      additionalChargeEmailNotes
    )
    if (error !== 'success') {
      msgsMgr.add(error.message, 'negative')
    }
    refetch()
    additionalChargeModalToggler.off()
    setAdditionalChargeName('')
    setAdditionalChargePriceText('')
    setAdditionalChargeEmailNotes('')
    setAdditionalChargeEmailCheckbox(true)
    spinner.off()
  }

  const [applyCustomerDiscount] = useApplyCustomerDiscountMutation({ client })

  const canChangeGearflowShipping =
    StoreOrderM.getShipments(storeOrderOld).length === 0 &&
    !['CANCELLED', 'REFUNDED'].includes(storeOrderOld.state) &&
    !(
      storeOrderOld.paymentMethod === PaymentMethod.Direct ||
      storeOrderOld.paymentMethod === PaymentMethod.Invoice
    )

  const markInvoiceAsPaid = async () => {
    try {
      spinner.on()

      await invoicePaidMutation({ variables: { storeOrderId: storeOrderOld.id } })
      await refetch()

      spinner.off()
      markPaidModalToggler.off()

      const message = 'Invoice paid!'
      msgsMgr.add(message, 'positive')
    } catch (error) {
      const err = error as { graphQLErrors: { message: string }[] }
      const message = err.graphQLErrors.reduce((acc, { message: msg }) => `${acc}${msg}`, '')

      spinner.off()
      markPaidModalToggler.off()

      msgsMgr.add(message, 'negative')
    }
  }

  const closeBuyerOfflineModal = () => {
    refetch()
    offlineTransactions.refetch()
    offlineBuyerTransactionModalToggler.off()
  }

  const closeOfflineSupplyModal = () => {
    offlineTransactions.refetch()
    offlineTransactionModalToggler.off()
  }

  const approveTitle =
    storeOrderOld.paymentMethod === PaymentMethod.Direct
      ? 'Approve Direct Pay Order'
      : 'Approve Order'
  const isDelivery = storeOrderOld?.curriDeliveries?.length > 0

  const showAddCharge =
    storeOrderOld.paymentMethod !== PaymentMethod.Direct &&
    !storeOrderOld.stripePayments &&
    StoreOrderM.isApprovedBySupplier(storeOrderOld)

  return (
    <>
      <OfflineBuyerTransactionModalForm
        storeOrderId={storeOrderOld.id}
        open={offlineBuyerTransactionModalOpen}
        onClose={closeBuyerOfflineModal}
        onSubmit={() => console.log('submitting paid invoice offline')}
      />

      <OfflineTransactionModalForm
        storeOrderId={storeOrderOld.id}
        open={offlineTransactionModalOpen}
        onClose={closeOfflineSupplyModal}
        onSubmit={() => console.log('submitting paid invoice offline')}
      />

      <ModalForm
        title="Send Invoice"
        submitButtonText="Send"
        submitButtonShowSpinner={spinnerLive}
        submitButtonDisabled={spinnerLive}
        open={invoiceModalOpen}
        onClose={invoiceModalToggler.off}
        onSubmit={(e) => {
          e.preventDefault()
          if (!invoiceDueDate) {
            approveModalToggler.off()
            msgsMgr.add('Error uploading Invoice. Please supply a Due Date.')
            return
          }
          spinner.on()
          uppy
            .upload()
            .then((files) => {
              if (files.successful.length < 1) {
                invoiceModalToggler.off()
                msgsMgr.add('Error uploading invoice file', 'negative')
                spinner.off()
                return
              }
              const invoiceUrl = files.successful[0].uploadURL
              uploadInvoicePayDirect({
                variables: {
                  storeOrderId: storeOrderOld.id,
                  invoiceUrl,
                  invoiceDueDate: TimeM.toPayloadNotNull(invoiceDueDate),
                  vendorUserId: null,
                },
              })
                .then(() => {
                  msgsMgr.add('Invoice uploaded. Email sent to customer.', 'positive')
                  refetch()
                })
                .catch((error: ApolloError) => {
                  msgsMgr.add(error.message, 'negative')
                })
                .finally(() => {
                  invoiceModalToggler.off()
                  spinner.off()
                })
            })
            .catch(() => {
              invoiceModalToggler.off()
              msgsMgr.add('Error uploading invoice file', 'negative')
              spinner.off()
            })
        }}
      >
        <div className="flex flex-col space-y-6">
          <p className="my-2 text-sm text-gray-700">
            This sends an email with the Invoice attached to the Customer.
          </p>
          <InvoiceInput
            uppy={uppy}
            dueDate={invoiceDueDate}
            onDueDateChange={setInvoiceDueDate}
            onFileInputError={(error) => msgsMgr.add(error, 'negative')}
          />
        </div>
      </ModalForm>

      <ModalForm
        title="Replace invoice document"
        submitButtonText="Upload replacement file"
        submitButtonShowSpinner={spinnerLive}
        submitButtonDisabled={spinnerLive}
        open={replaceInvoiceModalOpen}
        onClose={() => {
          // Drop any queued file so a reopened modal never silently re-uploads
          // a stale selection — the swap is irreversible and audit-stamped.
          replaceUppy.cancelAll()
          replaceInvoiceModalToggler.off()
        }}
        onSubmit={(e) => {
          e.preventDefault()
          spinner.on()
          replaceUppy
            .upload()
            .then((files) => {
              if (files.successful.length < 1) {
                msgsMgr.add('Please choose a replacement file.', 'negative')
                spinner.off()
                return
              }
              const invoiceUrl = files.successful[0].uploadURL
              replaceInvoiceDocument({
                variables: { storeOrderId: storeOrderOld.id, invoiceUrl },
              })
                .then(() => {
                  msgsMgr.add('Invoice document replaced. The order was not changed.', 'positive')
                  refetch()
                })
                .catch((error: ApolloError) => msgsMgr.add(error.message, 'negative'))
                .finally(() => {
                  replaceUppy.cancelAll()
                  replaceInvoiceModalToggler.off()
                  spinner.off()
                })
            })
            .catch(() => {
              msgsMgr.add('Error uploading replacement file', 'negative')
              spinner.off()
            })
        }}
      >
        <div className="flex flex-col space-y-4 text-sm text-gray-700">
          <p>
            This swaps <span className="font-medium">only the invoice file</span> on this order for
            the one you upload. Use it when the wrong file was uploaded and you have the correct
            one.
          </p>
          <p>
            <span className="font-medium">What this does:</span> replaces the downloadable invoice
            file and the links that point to it.
          </p>
          <p>
            <span className="font-medium">What this does not do:</span> it does not read or extract
            the new file, and it does not change the order — line items, totals, due date, and
            payment status all stay exactly as they are. No notification is sent to the customer or
            supplier.
          </p>
          <p className="text-gray-500">
            If the order details themselves are wrong, this is not the tool — edit the order
            instead.
          </p>
          <input
            required
            type="file"
            accept={attachmentAllowedFileTypes.join(',')}
            className="p-2 text-sm rounded-md border-gray-300 border shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            onChange={(event) => {
              const file = Array.from(event.target.files || [])[0]
              if (!file) return
              try {
                replaceUppy.cancelAll()
                replaceUppy.addFile({
                  source: 'file input',
                  name: file.name,
                  type: file.type,
                  data: file,
                })
              } catch (error) {
                const err = error as { isRestriction: boolean }
                msgsMgr.add(err.isRestriction ? `${err}` : 'Error attaching file', 'negative')
              }
            }}
          />
        </div>
      </ModalForm>

      <EmailActionModalForm
        title={approveTitle}
        open={approveModalOpen}
        onClose={approveModalToggler.off}
        onSubmit={(e) => {
          e.preventDefault()
          approveAction()
        }}
        submitButtonText="Approve"
        isWaiting={spinnerLive}
        emailText={approvedEmailAdditionalCommentsText}
        setEmailText={setApprovedEmailAdditionalCommentsText}
        sendEmailCheckbox={approvedEmailCheckbox}
        setSendEmailCheckbox={setApprovedEmailCheckbox}
      >
        <p className="mb-2">
          {storeOrderOld.paymentMethod !== PaymentMethod.Invoice &&
            storeOrderOld.paymentMethod !== PaymentMethod.Direct &&
            'Approving the order confirms this order has been accepted and will charge the customer.'}
        </p>
        <p className="mb-2">
          {storeOrderOld.paymentMethod === PaymentMethod.Direct &&
            'Approving the order confirms this order has been accepted and you are responsible for collecting payment from the buyer directly. Once approved you will have the opportunity to send an invoice to the customer.'}
        </p>
        <p className="mb-4">
          We recommend confirming fitment with the customer on all part orders before approving the
          order.
        </p>
      </EmailActionModalForm>

      <EditShippingModalForm
        shipment={shipmentToEdit}
        closeShipmentEdit={() => setShipmentToEdit(undefined)}
        reloadStoreOrder={() => {
          refetch()
          timelineEventsResult.refetch()
        }}
      />

      <CancelOrderModal
        storeOrder={storeOrderOld}
        isOpen={cancelModalOpen}
        close={cancelModalToggler.off}
        onOk={refetch}
      />

      <Routes>
        <Route
          path="refund"
          element={
            <RefundOrderModal
              refundableAmount={refundableAmount}
              onRefund={() => {
                refetch()
                timelineEventsResult.refetch()
                offlineTransactions.refetch()
              }}
            />
          }
        />
      </Routes>

      <EmailActionModal
        title="Ship Order"
        open={!!shippingModalShipment}
        onClose={() => setShippingModalShipment(undefined)}
        onSubmit={() => {
          if (!shippingModalShipment) {
            msgsMgr.add('Whoops, something went wrong. Please contact support.', 'negative')
            return
          }
          spinner.on()
          markAsShipped({
            variables: {
              shipmentId: shippingModalShipment.id,
              storeOrderId: storeOrderOld.id,
              sendEmail: shippingEmailCheckbox,
              notes: shippingEmailAdditionalCommentsText,
            },
          }).then((response) => {
            if (response.errors) {
              msgsMgr.add('Whoops, something went wrong. Please contact support.', 'negative')
            } else {
              setShippingEmailAdditionalCommentsText('')
              msgsMgr.add('Shipped', 'positive')
            }
            refetch()
            spinner.off()
            setShippingModalShipment(undefined)
          })
        }}
        isWaiting={spinnerLive}
        emailText={shippingEmailAdditionalCommentsText}
        setEmailText={setShippingEmailAdditionalCommentsText}
        sendEmailCheckbox={shippingEmailCheckbox}
        setSendEmailCheckbox={setShippingEmailCheckbox}
      />

      <EmailActionModalForm
        title="Additional charge"
        open={additionalChargeModalOpen}
        onClose={additionalChargeModalToggler.off}
        onSubmit={(e) => {
          e.preventDefault()
          addAdditionalChargeAction()
        }}
        submitButtonText="Submit"
        isWaiting={spinnerLive}
        emailText={additionalChargeEmailNotes}
        setEmailText={setAdditionalChargeEmailNotes}
        sendEmailCheckbox={additionalChargeEmailCheckbox}
        setSendEmailCheckbox={setAdditionalChargeEmailCheckbox}
      >
        {spinnerLive ? (
          <Spinner />
        ) : (
          <div className="pt-4 grid sm:grid-cols-2 grid-cols-1 sm:gap-y-0 gap-y-4 gap-x-4">
            <TextInput
              required
              placeholder="Name"
              value={additionalChargeName}
              onChange={(e) => setAdditionalChargeName(e.target.value)}
            />
            <PriceInput
              required
              placeholder="Price"
              min={0}
              value={additionalChargePriceText}
              onChange={(e) => setAdditionalChargePriceText(e.target.value)}
            />
          </div>
        )}
      </EmailActionModalForm>

      <Modal
        title="Mark Invoice Paid"
        open={markPaidModalOpen}
        onClose={markPaidModalToggler.off}
        withIcon
        footerAction={<Button title="Yes" onClick={markInvoiceAsPaid} />}
        cancelText="No"
      >
        <p>Clicking yes will mark this order as paid</p>
      </Modal>

      <Modal
        title="Pending Charges"
        open={alertPendingCharges}
        onClose={pendingChargesAlerter.off}
        withIcon
        footerAction={
          <Button
            title="Yes"
            onClick={() => {
              if (storeOrderOld.gearflowShipping) {
                navigate(`/orders/${storeOrderOld.id}/configure-shipment`)
              } else {
                navigate(`/orders/${storeOrderOld.id}/shipping`)
              }
            }}
          />
        }
        cancelText="No"
      >
        <p>
          {`There is still a pending additional charge on this order. Are you sure you'd like to
          create a shipment?`}
        </p>
      </Modal>

      <Modal
        title={`${storeOrderOld.gearflowShipping ? 'Disable' : 'Enable'} Gearflow Shipping`}
        open={changeGearflowShippingModalOpen}
        onClose={changeGearflowShippingModalToggle.off}
        footerAction={
          <Button
            title={storeOrderOld.gearflowShipping ? 'Disable' : 'Enable'}
            onClick={() => {
              spinner.on()
              setGearflowShippingMutation({
                variables: {
                  storeOrderId: storeOrderOld.id,
                  gearflowShipping: !storeOrderOld.gearflowShipping,
                },
              })
                .then(() => {
                  spinner.off()
                  changeGearflowShippingModalToggle.off()
                  refetch()
                })
                .catch((error) => {
                  spinner.off()
                  changeGearflowShippingModalToggle.off()
                  if (error.message) {
                    msgsMgr.add(error.message, 'negative')
                  } else {
                    msgsMgr.add('Whoops, something went wrong. Please contact support.', 'negative')
                  }
                })
            }}
          />
        }
      >
        {spinnerLive ? (
          <Spinner />
        ) : storeOrderOld.gearflowShipping ? (
          <p className="text-sm text-gray-700">
            Disable Gearflow shipping if you want to pay for shipping through your own provider or
            need to do an LTL shipment.
          </p>
        ) : (
          <>
            <p className="text-sm text-gray-700">
              Gearflow shipping enables suppliers to ship on Gearflow&apos;s account, meaning all
              you have to do is print the label and Gearflow does the rest.
            </p>
            <p className="mt-2 text-sm text-gray-700">
              At the moment, Gearflow shipping only supports UPS ground shipping.
            </p>
          </>
        )}
      </Modal>

      <EmailActionModal
        title="Ready"
        open={readyForPickupModalOpen}
        onClose={readyForPickupModalToggler.off}
        onSubmit={() => {
          spinner.on()
          storeOrderReadyForPickup({
            variables: {
              storeOrderId: storeOrderOld.id,
              notes: readyForPickupEmailNotes,
              sendEmail: readyForPickupEmailCheckbox,
            },
          })
            .then(() => {
              const message = `Order ready.${
                readyForPickupEmailCheckbox ? ' Email sent to customer.' : ''
              }`
              msgsMgr.add(message, 'positive')
              setReadyForPickupEmailNotes('')
              setReadyForPickupEmailCheckbox(true)

              refetch().catch(() => {
                msgsMgr.add('Error reloading order, please refresh the page.')
              })
            })
            .catch((e: ApolloError) => {
              msgsMgr.add(e.message, 'negative')
            })
            .finally(() => {
              readyForPickupModalToggler.off()
              spinner.off()
            })
        }}
        isWaiting={spinnerLive}
        emailText={readyForPickupEmailNotes}
        setEmailText={setReadyForPickupEmailNotes}
        sendEmailCheckbox={readyForPickupEmailCheckbox}
        setSendEmailCheckbox={setReadyForPickupEmailCheckbox}
      >
        <div className="mb-4">
          This sends an email to the customer notifying them that their Order is ready to be picked
          up.
        </div>
      </EmailActionModal>

      <Routes>
        <Route
          path="complete"
          element={<CompleteOrder storeOrderId={storeOrderOld.id} onCompleted={refetch} />}
        />

        <Route path="upload-proof-of-delivery" element={<UploadPod />} />
      </Routes>

      <div className="-mt-2">
        <Layout>
          <Layout.Section type="primary">
            {storeOrderOld.problems &&
              storeOrderOld.problems.filter(StoreOrderM.isStoreOrderProblemOpen).length > 0 && (
                <div className="flex flex-col gap-y-2">
                  {storeOrderOld.problems
                    .filter(StoreOrderM.isStoreOrderProblemOpen)
                    .map((problem) => (
                      <StoreOrderProblem
                        problem={problem}
                        toContactCustomer={`/orders/${storeOrderOld.id}/messages`}
                      />
                    ))}
                </div>
              )}

            {StoreOrderM.isApprovedBySupplier(storeOrderOld) &&
              storeOrderOld.paymentMethod === PaymentMethod.Direct &&
              storeOrderOld.invoices?.length === 0 && (
                <Banner text="Send Invoice" buttonText="Upload" onClick={invoiceModalToggler.on} />
              )}

            {/* Admin-only mirror of "Send Invoice": swap the file behind an
                existing invoice (GEA-3892). File only — no re-extraction, no
                order edit, no notification. */}
            {UserM.isAdmin(user) &&
              storeOrderOld.paymentMethod === PaymentMethod.Direct &&
              (storeOrderOld.invoices?.length ?? 0) > 0 && (
                <Banner
                  text="Replace invoice document"
                  buttonText="Replace"
                  onClick={replaceInvoiceModalToggler.on}
                />
              )}

            {storeOrder &&
              storeOrder.vendorFulfilledAt &&
              storeOrder.shipments.length > 0 &&
              storeOrder.proofOfDeliveries.length === 0 && (
                <Banner
                  text="Upload Proof of Delivery"
                  icon={ArrowUpTrayIcon}
                  action={
                    <Link.S to={`/orders/${storeOrder.id}/upload-proof-of-delivery`}>Upload</Link.S>
                  }
                />
              )}

            <OrderItemCards
              storeOrder={storeOrderOld}
              onCancelStoreOrder={cancelModalToggler.on}
              onApproveStoreOrder={approveModalToggler.on}
              onCreateShipment={(gearflowShipping) => {
                if (storeOrderOld.additionalCharges.some((ac) => ac.state === 'pending')) {
                  pendingChargesAlerter.on()
                } else if (gearflowShipping) {
                  navigate(`/orders/${storeOrderOld.id}/configure-shipment`)
                } else {
                  navigate(`/orders/${storeOrderOld.id}/shipping`)
                }
              }}
              onMarkAsShipped={(shipment) => setShippingModalShipment(shipment)}
              onReadyForPickup={() => readyForPickupModalToggler.on()}
              setShipmentToEdit={setShipmentToEdit}
            />

            {storeOrderOld.accountMachines.length !== 0 && (
              <Card title={pluralize('Machines', storeOrderOld.accountMachines.length)}>
                <Card.Section>
                  <div className="rounded border border-gray-300 overflow-x-scroll">
                    <AccountMachinesTable accountMachines={storeOrderOld.accountMachines} />
                  </div>
                </Card.Section>
              </Card>
            )}

            <Card title={paymentCardTitle}>
              <Card.Section>
                <Card.SubSection>
                  <div className="flex flex-col space-y-2 text-sm text-gray-700">
                    <div className="flex space-x-4">
                      <span>Subtotal</span>
                      <span className="flex-1">{`${storeOrderOld.lineItems.length} ${pluralize(
                        'items',
                        storeOrderOld.lineItems.length
                      )}`}</span>

                      <span>{MoneyM.format(subtotal)}</span>
                    </div>

                    <div className="flex justify-between">
                      {isDelivery ? (
                        <span>Delivery</span>
                      ) : storeOrderOld.pickup ? (
                        <span>Pickup</span>
                      ) : (
                        <span>Shipping</span>
                      )}
                      <span>
                        {isDelivery
                          ? null
                          : storeOrderOld.pickup
                            ? StoreOrderM.SHIPPING_PICKUP_LINE_ITEM
                            : MoneyM.format(storeOrderOld.shippingAmount)}
                      </span>
                    </div>

                    {storeOrderOld.paymentMethod !== PaymentMethod.Direct &&
                      !MoneyM.isZero(convenienceFee) && (
                        <div className="flex justify-between">
                          <span>Customer Fee</span>
                          <span>{MoneyM.format(convenienceFee)}</span>
                        </div>
                      )}

                    {!MoneyM.isZero(storeOrderOld.salesTax) && (
                      <div className="flex justify-between">
                        <span>Sales Tax</span>
                        <span>{MoneyM.format(storeOrderOld.salesTax)}</span>
                      </div>
                    )}

                    {storeOrderOld.additionalCharges.map((additionalCharge) => (
                      <div key={additionalCharge.id} className="flex justify-between items-center">
                        <div
                          className={`${
                            additionalCharge.state === 'pending' ? 'text-gray-500' : ''
                          }`}
                        >
                          <span>{additionalCharge.name}</span>
                          {additionalCharge.state === 'pending' && (
                            <>
                              <span className="ml-4 italic">({additionalCharge.state})</span>
                              {UserM.isAdmin(user) && (
                                <a
                                  className="ml-4 text-blue-500 underline cursor-pointer"
                                  href={`${buyersUrl}/orders/${storeOrderOld.id}?additionalChargeId=${additionalCharge.id}`}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Review charge
                                </a>
                              )}
                            </>
                          )}
                          <div className="inline-flex ml-4">
                            {UserM.isAdmin(user) &&
                              additionalCharge.state !== 'pending' &&
                              additionalCharge.invoice &&
                              !additionalCharge.invoice.isCanceled && (
                                <ButtonLink
                                  primary={false}
                                  size="sm"
                                  href={`${adminUrl}/invoices${QueryString.invoicesStoreOrderId(
                                    storeOrderOld.shortId
                                  )}`}
                                >
                                  {InvoiceM.isPaymentRequired(additionalCharge.invoice)
                                    ? 'Pay Invoice'
                                    : 'View Invoice'}
                                </ButtonLink>
                              )}
                          </div>
                        </div>
                        <span>{MoneyM.format(additionalCharge.price)}</span>
                      </div>
                    ))}

                    {showAddCharge && (
                      <div>
                        <span
                          className="text-blue-500 underline cursor-pointer"
                          onClick={() => additionalChargeModalToggler.on()}
                        >
                          Add additional charge
                        </span>
                      </div>
                    )}
                  </div>
                </Card.SubSection>
                <Card.SubSection>
                  <div className="flex justify-between text-sm text-gray-700 font-bold">
                    <span>Total</span>
                    <span>{MoneyM.format(total)}</span>
                  </div>
                </Card.SubSection>
                {!(
                  storeOrderOld.paymentMethod === PaymentMethod.Direct ||
                  storeOrderOld.paymentMethod === PaymentMethod.Invoice
                ) && (
                  <Card.SubSection>
                    <div className="flex justify-between text-sm text-gray-700">
                      <span>Paid by customer</span>
                      <span>
                        {MoneyM.format(isUnpaid ? MoneyM.fromDecimal(0, 'USD') : paidTotal)}
                      </span>
                    </div>
                  </Card.SubSection>
                )}
                {storeOrderOld.refunds.length > 0 && (
                  <Card.SubSection>
                    <div className="flex flex-col space-y-2">
                      <div className="flex justify-between text-sm text-gray-700">
                        <span>Refunded</span>
                        <span>{MoneyM.format(MoneyM.mult(totalRefunded, -1))}</span>
                      </div>
                      <div className="flex justify-between text-sm text-gray-700 font-bold">
                        {storeOrderOld.paymentMethod === PaymentMethod.Direct ? (
                          <>
                            <span>
                              <strong>Net total</strong>
                            </span>
                            <span>{MoneyM.format(netTotal)}</span>
                          </>
                        ) : (
                          <>
                            <span>
                              <strong>Net payment</strong>
                            </span>
                            <span>{MoneyM.format(netCustomerPayment)}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </Card.SubSection>
                )}
              </Card.Section>
            </Card>
            {!(
              storeOrderOld.paymentMethod === PaymentMethod.Direct ||
              storeOrderOld.paymentMethod === PaymentMethod.Invoice
            ) &&
              storeOrderOld.store.gearflowPaymentsEnabled && (
                <Card
                  // TODO: When paying on account is an option for the order, add copy somewhere in this card about payout
                  // only being for "Gearflow payments"
                  title={
                    StoreOrderM.isUnapprovedBySupplier(storeOrderOld) ? 'Expected payout' : 'Payout'
                  }
                  headerButton={
                    !StoreOrderM.isUnapprovedBySupplier(storeOrderOld) ? (
                      <Link.T
                        to={`/transactions${QueryString.transactionStoreOrderId(
                          storeOrderOld.shortId
                        )}`}
                      >
                        View transactions
                      </Link.T>
                    ) : undefined
                  }
                >
                  <Card.Section>
                    <Card.SubSection>
                      <div className="flex flex-col space-y-2">
                        <div className="flex justify-between text-sm text-gray-700">
                          <div className="flex justify-center">
                            <span>Payout eligible</span>
                            <Tooltip
                              className="inline-block ml-1"
                              text={`The order total minus customer fees${
                                storeOrderOld.gearflowShipping
                                  ? ', sales tax, and shipping costs'
                                  : ' and sales tax'
                              }.`}
                              size="small"
                            />
                          </div>
                          <span>{MoneyM.format(payoutEligible)}</span>
                        </div>
                        <div className="flex justify-between text-sm text-gray-700">
                          <div className="flex justify-center">
                            <span>Fees</span>
                            <Tooltip
                              className="inline-block ml-1"
                              text="Gearflow fee + credit card processing fee."
                              size="small"
                            />
                          </div>
                          <span>{MoneyM.format(MoneyM.mult(gearflowFee, -1))}</span>
                        </div>
                      </div>
                    </Card.SubSection>
                    <Card.SubSection>
                      <div className="flex justify-between text-sm text-gray-700 font-bold">
                        <span>Payout</span>
                        <span>{MoneyM.format(payout)}</span>
                      </div>
                    </Card.SubSection>
                  </Card.Section>
                </Card>
              )}

            {storeOrderOld.features.storeOrderTimelineV1 && (
              <TimelineCard
                storeOrder={storeOrderOld}
                reloadStoreOrder={refetch}
                storeOrderEvents={eventsSearch?.storeOrderEvents}
                rfqEvents={eventsSearch?.rfqEvents}
                openMessagesTab={() => navigate(`/orders/${storeOrderId}/messages`)}
              />
            )}
          </Layout.Section>

          <Layout.Section type="secondary">
            <ProofOfDeliveries />

            {storeOrderOld.purchaseOrder && (
              <Card title="Purchase Order">
                <Card.Section>
                  <p className="text-sm text-gray-500">
                    Purchase Order: {storeOrderOld.purchaseOrder}
                  </p>
                </Card.Section>
              </Card>
            )}

            {UserM.isAdmin(user) &&
              storeOrderOld.paymentMethod === PaymentMethod.Invoice &&
              storeOrderOld.paymentStatus === 'unpaid' &&
              StoreOrderM.isApprovedBySupplier(storeOrderOld) && (
                <Card
                  title="Enterprise Accounts"
                  primaryFooterAction={{
                    id: 1,
                    title: 'Mark Invoice Paid',
                    callBack: markPaidModalToggler.on,
                  }}
                >
                  <Card.Section>
                    <p>This order was paid on invoice and is not yet paid</p>
                  </Card.Section>
                </Card>
              )}

            {UserM.isAdmin(user) && (
              <OfflineTransactionCard
                offlineTransactions={offlineTransactions}
                storeOrderState={storeOrderOld.state}
                offlineTransactionModalToggler={offlineTransactionModalToggler}
                offlineBuyerTransactionModalToggler={offlineBuyerTransactionModalToggler}
              />
            )}

            {!storeOrderOld.store.shipsFree &&
              storeOrderOld.paymentMethod !== PaymentMethod.Direct &&
              !storeOrderOld.pickup &&
              storeOrderOld.store.gearflowPaymentsEnabled && (
                <Card
                  title="Gearflow shipping"
                  headerButton={
                    <Tooltip
                      text={[
                        "Gearflow shipping enables suppliers to ship on Gearflow's account, meaning all you have to do is print the label and Gearflow does the rest.",
                        'At the moment, Gearflow shipping only supports UPS ground shipping.',
                      ]}
                      left
                      size="medium"
                    />
                  }
                >
                  <Card.Section>
                    <Checkbox
                      label="Gearflow shipping"
                      checked={storeOrderOld.gearflowShipping}
                      onChange={() => changeGearflowShippingModalToggle.on()}
                      disabled={!canChangeGearflowShipping}
                    />
                    <p className="text-sm text-gray-500">
                      {storeOrderOld.paymentMethod === PaymentMethod.Invoice
                        ? 'Gearflow shipping cannot be enabled for orders paid on invoice.'
                        : 'Gearflow shipping cannot be changed after a shipment is created.'}
                    </p>
                  </Card.Section>
                </Card>
              )}
            {storeOrderOld.order.requestForQuote && (
              <Card title="RFQ">
                <Card.Section>
                  <Link.T to={`/rfqs/${storeOrderOld.order.requestForQuote.id}`}>
                    {RequestForQuoteContext.shortenId(storeOrderOld.order.requestForQuote.id)}
                  </Link.T>
                </Card.Section>
              </Card>
            )}
            <Card title="Notes">
              <Card.Section>
                <p className="text-sm text-gray-500">
                  {storeOrderOld.order.comment ?? 'No notes from customer.'}
                </p>
              </Card.Section>
            </Card>
            {!storeOrderOld.order.requestForQuote && storeOrderOld.timingDetails && (
              <Card title="Expected Shipping / Lead Time">
                <Card.Section>
                  <p className="text-sm text-gray-500">{storeOrderOld.timingDetails}</p>
                </Card.Section>
              </Card>
            )}
            <CustomerCard
              storeOrder={storeOrderOld}
              billingAddress={storeOrder.billingAddress}
              company={storeOrder.company}
              spinnerLive={spinnerLive}
              spinner={spinner}
              reloadStoreOrder={refetch}
            />
            {UserM.isAdmin(user) && (
              <Card title="Notifications (Buyer)">
                <Card.Section>
                  {storeOrderOld.buyerNotificationUsers.length === 0 ? (
                    <p className="text-sm text-gray-700">None.</p>
                  ) : (
                    storeOrderOld.buyerNotificationUsers.map(({ id, name }) => (
                      <p key={id} className="text-sm text-gray-700">
                        {name}
                      </p>
                    ))
                  )}
                </Card.Section>
              </Card>
            )}
            {UserM.isAdmin(user) &&
              storeOrderOld.paymentMethod !== PaymentMethod.Invoice &&
              storeOrderOld.paymentMethod !== PaymentMethod.Direct &&
              storeOrderOld?.payoutRate <= 0.8735 &&
              (storeOrderOld.state === 'RECEIVED' || storeOrderOld.state === 'OPEN_QUOTE') && (
                <Card
                  title="Apply customer discount"
                  primaryFooterAction={{
                    id: 1,
                    title: 'Apply',
                    showSpinner: spinnerLive,
                    disabled: spinnerLive,
                    callBack: () => {
                      spinner.on()

                      return applyCustomerDiscount({
                        variables: { storeOrderId: storeOrderOld.id },
                      })
                        .then(() => {
                          refetch()
                          msgsMgr.add('Discount applied!', 'positive')
                        })
                        .catch((mutationError: GraphQLError) =>
                          msgsMgr.add(mutationError.message, 'negative')
                        )
                        .finally(() => {
                          spinner.off()
                        })
                    },
                  }}
                >
                  <Card.Section>
                    <p>This applies the vendor payout rate by linking the customer and supplier</p>
                  </Card.Section>
                </Card>
              )}
          </Layout.Section>
        </Layout>
      </div>
    </>
  )
}

export default StoreOrderDetails
